import React, { ReactNode, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  Globe,
  Heart,
  Instagram,
  Laptop,
  Shield,
  Smartphone,
  Sparkles,
  Star,
  Tablet,
  Users,
} from "lucide-react";
import AppHeader from "./AppHeader";
import { ConsteladorEntryCard } from "./ConsteladorEntryCard";
import TestimonialsCarousel from "./TestimonialsCarousel";
import { useInView } from "../hooks/useInView";
import { captureInfluencerRefFromSearch } from "../utils/influencerAttribution";
import {
  CONSTELADOR_CLUB_URL,
  PUBLIC_CONSTELLATION_GROUP_URL,
} from "../constants/consteladorLanding";

const WHATSAPP_GROUP = PUBLIC_CONSTELLATION_GROUP_URL;
const INSTAGRAM_URL = "https://www.instagram.com/constelabrasiloficial";
const MARKETING_VIDEO_ID = "7p2qhXkqwo8";

function Reveal({
  children,
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`mkt-reveal ${visible ? "mkt-reveal--visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const paths = [
  {
    id: "constelacao",
    icon: Heart,
    title: "Quero Fazer Minha Constelação Familiar",
    paragraphs: [
      "Você sente que algo se repete na sua vida, como se certos bloqueios, conflitos ou dificuldades insistissem em voltar?",
      "Aqui, você encontra profissionais certificados, qualificados e homologados pela plataforma, preparados para conduzir sua Constelação com segurança, profundidade e acolhimento.",
      "Faça sua sessão online, de onde estiver, com a mesma seriedade e conexão de um atendimento presencial, em um ambiente seguro e profissional.",
    ],
    cta: "Agendar Minha Sessão",
    to: "/terapeutas",
    accent: "from-[#2B5A41] to-[#203C2E]",
  },
  {
    id: "participar",
    icon: Users,
    title: "Quero Conhecer e Participar de uma Sessão",
    paragraphs: [
      "Nunca fez uma Constelação e quer entender como funciona?",
      "Participe de uma sessão aberta como observador ou representante e vivencie, na prática, a profundidade e a força transformadora dessa experiência.",
      "Mesmo sem trazer o seu próprio tema, cada participação pode gerar insights importantes para a sua vida, porque ao observar outras histórias e ocupar um lugar no campo, muitas vezes reconhecemos padrões, emoções e dinâmicas que também fazem parte da nossa própria trajetória.",
      "Uma forma segura, acolhedora e acessível de conhecer a Constelação antes de dar o próximo passo.",
    ],
    cta: "Para participar gratuitamente",
    to: "/#grupo-avisos",
    accent: "from-[#2B5A41] to-[#5F7167]",
  },
];

const devices = [
  { icon: Laptop, label: "Computador", desc: "Experiência completa no navegador" },
  { icon: Smartphone, label: "Celular", desc: "Acesso de qualquer lugar" },
  { icon: Tablet, label: "Tablet", desc: "Tela ampla para maior imersão" },
];

const pillars = [
  {
    icon: Sparkles,
    title: "Experiência Profunda",
    text: "Participe ativamente e vivencie a profundidade das constelações como no presencial.",
  },
  {
    icon: Shield,
    title: "Segurança Garantida",
    text: "Diálogo privado com o constelador, garantindo sigilo e conforto durante a experiência.",
  },
  {
    icon: Globe,
    title: "Participação Ativa",
    text: "Movimente seu avatar e contribua para o campo de forma interativa e envolvente.",
  },
];

const whatIsConstellationParagraphs = [
  "Ela é uma terapia, uma técnica terapêutica que ajuda a compreender questões emocionais e padrões que se repetem na vida das pessoas. Às vezes existe uma confusão: algumas pessoas acham que Constelação tem relação com religião ou pastoral, mas não tem nada a ver com religiosidade.",
  "A técnica foi desenvolvida pelo Bert Hellinger, um terapeuta alemão que, antes, foi padre por muitos anos. Ele criou a Constelação ao perceber como certos padrões de comportamento e acontecimentos se repetem de geração em geração. Essa percepção surgiu enquanto ele trabalhava com comunidades Zulu, na África, onde observou como essas repetições influenciavam a vida das pessoas.",
  "A Constelação Familiar é uma ferramenta que nos permite visualizar essas dinâmicas ocultas nas famílias, ajudando a identificar a raiz de conflitos, dificuldades emocionais ou padrões repetitivos, e promovendo soluções para essas questões que muitas vezes poderiam levar anos de terapia para aparecer. Por exemplo, algumas pessoas tentam de tudo para mudar determinadas situações, mas continuam enfrentando problemas como dificuldades financeiras, relacionamentos que não dão certo, medos inexplicáveis ou compulsões. A Constelação ajuda a entender essas questões e reprogramá-las, trazendo autoconhecimento, bem-estar e transformações para aqueles que buscam uma vida mais feliz.",
  "Hellinger desenvolveu originalmente a Constelação de forma presencial, usando a fenomenologia e o campo morfogenético, um campo de informação que conecta as pessoas e transmite informações da família e da espécie. Para ilustrar, há estudos como o caso de macacos que aprenderam a lavar frutas antes de comer. Quando uma quantidade significativa deles repetiu essa ação, outros macacos em ilhas próximas começaram a fazer o mesmo, mostrando que existe uma conexão invisível de informações dentro da espécie. O mesmo acontece com tartarugas que, ao nascerem, já sabem a direção do mar: são informações passadas geneticamente e através da conexão da espécie.",
  "Na Constelação em grupo, cada participante representa um membro da família ou uma questão específica da pessoa que busca a constelação. Esses representantes se posicionam no espaço conforme a percepção, e a partir de seus sentimentos, gestos, reações e verbalizações, é possível observar as dinâmicas ocultas, os vínculos e os bloqueios familiares. As palavras que cada representante expressa trazem informações valiosas sobre emoções e relacionamentos que muitas vezes permanecem inconscientes.",
  "Essas informações se tornam visíveis, revelando padrões que normalmente permanecem ocultos. É essa interação que dá à Constelação em grupo sua profundidade transformadora, pois o movimento, a posição e as verbalizações de cada representante permitem identificar causas e encontrar caminhos de reconciliação, pertencimento e harmonia dentro do sistema familiar.",
  "Com o tempo, surgiram adaptações, como a Constelação Individual, que usa bonequinhos para representar familiares, e durante a pandemia, a Constelação Online via videoconferência, que também mostrou resultados eficazes.",
  "E é aqui que entra a Constela Brasil, a primeira plataforma de Constelação online, em grupo, com a mesma profundidade do presencial.",
  "Ela une a tecnologia digital com a profundidade da Constelação presencial: cada participante entra em um cenário virtual, escolhe um avatar e participa da constelação como se estivesse presente fisicamente. Isso mantém a força e a dinâmica do grupo, trazendo mais imersão e profundidade, seja para atendimentos individuais ou em grupo, aproximando a experiência digital da experiência presencial.",
  "O objetivo é proporcionar bem-estar, soluções rápidas para padrões repetitivos e clareza emocional, de uma forma acessível e tecnológica, sem perder a essência transformadora da Constelação Familiar.",
];

const MarketingHome: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    captureInfluencerRefFromSearch(location.search);
  }, [location.search]);

  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (!hash) return;
    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className="cb-page min-h-screen bg-[#F5F6F0] text-[#203C2E]">
      <AppHeader />

      {/* Hero */}
      <section className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-20">
        <div className="mkt-hero-glow pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
            {/* Texto */}
            <div className="text-center lg:text-left">
              <Reveal>
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-[#89A78C]">
                  Para quem conduz · Para quem busca · Para quem quer conhecer
                </p>
              </Reveal>

              <Reveal delay={80}>
                <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight text-[#2B5A41] sm:text-5xl lg:text-6xl">
                  Constelação Familiar Online
                  <span className="cb-serif mt-2 block text-[#89A78C]">Individual ou em Grupo</span>
                </h1>
              </Reveal>

              <Reveal delay={160}>
                <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-[#5F7167] sm:text-xl lg:mx-0">
                  A primeira plataforma com campo virtual, avatares interativos e videochamada integrada,
                  desenvolvida exclusivamente para constelações familiares online.
                </p>
              </Reveal>

              <Reveal delay={240}>
                <div className="mt-6 flex justify-center lg:justify-start">
                  <div className="inline-flex items-center gap-1 rounded-full bg-[#2B5A41] px-4 py-2 text-sm font-semibold text-white">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                    <span className="ml-1">5 estrelas</span>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={300}>
                <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap lg:items-start lg:justify-start">
                  <Link
                    to="/terapeutas"
                    className="group inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-full bg-[#2B5A41] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#2B5A41]/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#203C2E] sm:w-auto"
                  >
                    Agendar Minha Sessão
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                  <a
                    href={WHATSAPP_GROUP}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-full bg-[#2B5A41] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#2B5A41]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#203C2E] sm:w-auto"
                  >
                    Participar grátis
                    <ArrowRight className="h-5 w-5" aria-hidden />
                  </a>
                  <Link
                    to="/constelador"
                    className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-full border-2 border-[#2B5A41] px-8 py-4 text-base font-semibold text-[#2B5A41] transition-all duration-300 hover:bg-[#2B5A41] hover:text-white sm:w-auto"
                  >
                    Sou Constelador
                  </Link>
                </div>
              </Reveal>
            </div>

            {/* Imagem da plataforma */}
            <Reveal delay={200}>
              <ConsteladorEntryCard to="/constelador#planos" />
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#2B5A41]/25 to-[#2B5A41]/10 blur-2xl"
                  aria-hidden="true"
                />
                <img
                  src="/assets/imagem_home.png"
                  alt="Plataforma Constela Brasil: campo virtual com avatares e videochamada integrada"
                  className="relative w-full rounded-[1.5rem] border border-[#E0EDE3] shadow-2xl"
                  loading="eager"
                />
              </div>
            </Reveal>
          </div>

          {/* Tecnologia a serviço da Alma */}
          <Reveal delay={120}>
            <div className="mt-16 sm:mt-20">
              <h2 className="text-center text-2xl font-bold text-[#2B5A41] sm:text-3xl">
                Tecnologia a serviço da Alma
              </h2>
              <div className="mkt-video mt-6">
                <iframe
                  src={`https://www.youtube.com/embed/${MARKETING_VIDEO_ID}?rel=0&modestbranding=1&playsinline=1`}
                  title="Tecnologia a serviço da Alma | Constela Brasil"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#2B5A41] py-8 sm:py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 px-4 text-center sm:px-6 md:flex-row md:text-left lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#E0EDE3]">
              Gratuito
            </p>
            <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">
              Quer conhecer ou participar de uma constelação?
            </h2>
            <p className="mt-2 max-w-xl text-base text-white/85">
              Entre no grupo e acompanhe os encontros, sem custo.
            </p>
          </div>
          <a
            href={WHATSAPP_GROUP}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-[#2B5A41] shadow-lg transition hover:-translate-y-0.5 hover:bg-[#F5F6F0]"
          >
            Entrar no grupo
            <ArrowRight className="h-5 w-5" aria-hidden />
          </a>
        </div>
      </section>

      {/* O que você procura */}
      <section id="caminhos" className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-[#2B5A41] sm:text-4xl">O Que Você Procura?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-[#5F7167]">
                A Constela Brasil foi criada para atender diferentes necessidades. Escolha o caminho que faz sentido para você.
              </p>
            </div>
          </Reveal>

          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
            {paths.map((path, index) => (
              <Reveal key={path.id} delay={index * 100}>
                <article className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-[#E0EDE3] bg-white shadow-[0_18px_40px_rgba(43,90,65,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_48px_rgba(43,90,65,0.12)]">
                  <div className={`bg-gradient-to-br ${path.accent} px-6 py-5`}>
                    <div className="mb-3 inline-flex rounded-2xl bg-white/15 p-3 backdrop-blur">
                      <path.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold leading-snug text-white">{path.title}</h3>
                  </div>
                  <div className="flex flex-1 flex-col px-6 py-6">
                    <div className="space-y-3 text-[15px] leading-relaxed text-[#5F7167]">
                      {path.paragraphs.map((p) => (
                        <p key={p.slice(0, 24)}>{p}</p>
                      ))}
                    </div>
                    <Link
                      to={path.to}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#89A78C] transition-colors group-hover:gap-3"
                    >
                      {path.cta}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sou Constelador e Quero Atender Online */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#89A78C]">
                  Para consteladores
                </p>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-[#2B5A41] sm:text-4xl">
                  Sou Constelador <span className="cb-serif text-[#89A78C]">e Quero Atender Online</span>
                </h2>
                <div className="mt-5 space-y-4 text-lg leading-relaxed text-[#5F7167]">
                  <p className="font-semibold text-[#2B5A41]">
                    Chega de improvisar com bonecos na câmera.
                  </p>
                  <p>
                    Conduza sessões individuais ou em grupo com campo virtual, avatares interativos e
                    videochamada integrada. Tudo em um só lugar, com a profundidade do presencial e o
                    conforto para você e seus clientes.
                  </p>
                  <p>
                    Atenda clientes em todo o Brasil e no mundo, com uma experiência mais profissional,
                    acolhedora e de maior autoridade, mantendo a força do trabalho presencial, com mais
                    organização, clareza e segurança no online.
                  </p>
                </div>
                <p className="mt-6 text-base font-medium text-[#2B5A41]">
                  Uma plataforma completa que conecta consteladores e clientes em um ambiente virtual
                  imersivo. Do cadastro ao acesso ao campo virtual, tudo acontece em segundos, sem
                  instalação, sem complicação.
                </p>
                <div className="mt-8 flex flex-col items-start gap-3">
                  <Link
                    to="/constelador#planos"
                    className="inline-flex items-center gap-2 rounded-full bg-[#2B5A41] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#203C2E]"
                  >
                    Ver planos para consteladores
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a
                    href={CONSTELADOR_CLUB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#2B5A41] underline decoration-[#89A78C] underline-offset-4 transition hover:text-[#203C2E]"
                  >
                    Se você é constelador, conheça o nosso Clube dos Consteladores
                    <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#2B5A41]/20 to-[#2B5A41]/10 blur-2xl"
                  aria-hidden="true"
                />
                <img
                  src="/assets/checkout/plataforma-preview.png"
                  alt="Prévia da plataforma Constela Brasil com campo virtual e videochamada"
                  className="relative w-full rounded-[1.5rem] border border-[#E0EDE3] shadow-xl"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Acesse pelo navegador */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-[#2B5A41] sm:text-4xl">
                Acesse pelo Navegador
                <span className="cb-serif mt-1 block text-[#89A78C]">Fácil de usar</span>
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-[#5F7167]">
                Sem downloads, sem instalação. Abra o link no computador, tablet ou celular e entre na sua sala virtual em segundos.
                A Constela Brasil foi projetada para funcionar em qualquer dispositivo com acesso à internet.
              </p>
            </div>
          </Reveal>

          <div className="grid gap-6 sm:grid-cols-3">
            {devices.map((device, index) => (
              <Reveal key={device.label} delay={index * 80}>
                <div className="rounded-[1.5rem] border border-[#E0EDE3] bg-white p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                  <div className="mx-auto mb-4 inline-flex rounded-2xl bg-[#E0EDE3] p-4">
                    <device.icon className="h-8 w-8 text-[#89A78C]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2B5A41]">{device.label}</h3>
                  <p className="mt-2 text-sm text-[#5F7167]">{device.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={200}>
            <div className="mt-12 text-center">
              <Link
                to="/terapeutas"
                className="inline-flex items-center gap-2 rounded-full bg-[#2B5A41] px-8 py-4 text-base font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-[#203C2E]"
              >
                <Calendar className="h-5 w-5" />
                Agende a Sua Constelação
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Por que confiar + depoimento */}
      <section className="bg-[#2B5A41] py-16 text-white sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold sm:text-4xl">Por Que Confiar na Constela Brasil?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80">
                Depoimentos de clientes que fizeram a constelação com um dos nossos consteladores homologados.
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <TestimonialsCarousel />
          </Reveal>

          <Reveal delay={140}>
            <div className="mt-12 space-y-5">
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-lg">
                <img
                  src="/assets/home/depoimentos/ig-stories.png"
                  alt="Stories de clientes sobre a experiência de constelação online na plataforma"
                  className="w-full"
                  loading="lazy"
                />
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-lg">
                  <img
                    src="/assets/home/depoimentos/ig-ramos-marce.png"
                    alt="Depoimentos no Instagram de @ramosdulcinea e @marcemarassa"
                    className="w-full"
                    loading="lazy"
                  />
                </div>
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-lg">
                  <img
                    src="/assets/home/depoimentos/ig-dres-erica.png"
                    alt="Depoimentos no Instagram de @dresantosmentora e @andrade.ericapriscila"
                    className="w-full"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={180}>
            <p className="mt-10 text-center text-sm text-white/70">
              Acompanhe, no nosso perfil do Instagram, consteladores que já utilizam a plataforma.
            </p>
            <div className="mt-4 flex justify-center">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-medium transition-all hover:bg-white/10"
              >
                <Instagram className="h-4 w-4" />
                @constelabrasiloficial
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Sobre nós + pilares */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div>
                <h2 className="text-3xl font-bold text-[#2B5A41] sm:text-4xl">Sobre Nós</h2>
                <p className="mt-5 text-lg leading-relaxed text-[#5F7167]">
                  Na Constela Brasil, trazemos constelações em grupo para o online, mantendo a mesma profundidade dos encontros presenciais.
                </p>
                <p className="mt-4 text-lg leading-relaxed text-[#5F7167]">
                  Nossa plataforma oferece vivências online interativas, com avatares e a comodidade de estar em casa,
                  garantindo privacidade e segurança.
                </p>
                <p className="mt-4 font-medium text-[#2B5A41]">
                  Vivencie constelações em grupo online com conforto e segurança na sua casa.
                </p>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#2B5A41]/10 to-[#2B5A41]/15 blur-2xl"
                  aria-hidden="true"
                />
                <img
                  src="/assets/checkout/ambiente-reuniao.png"
                  alt="Sessão de constelação em grupo online com avatares e videochamada"
                  className="relative w-full rounded-[1.5rem] border border-[#E0EDE3] shadow-lg"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {pillars.map((pillar, index) => (
              <Reveal key={pillar.title} delay={index * 80}>
                <div className="rounded-[1.5rem] border border-[#E0EDE3] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                  <div className="mb-4 inline-flex rounded-2xl bg-[#2B5A41]/10 p-3">
                    <pillar.icon className="h-6 w-6 text-[#89A78C]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2B5A41]">{pillar.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5F7167]">{pillar.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Constelação Familiar — o que é */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-center text-3xl font-bold text-[#2B5A41] sm:text-4xl">
              Constelação Familiar, o que é?
            </h2>
          </Reveal>

          <div className="mt-10 space-y-5">
            {whatIsConstellationParagraphs.map((paragraph, index) => (
              <Reveal key={paragraph.slice(0, 32)} delay={Math.min(index * 40, 200)}>
                <p className="text-[17px] leading-[1.85] text-[#5F7167]">{paragraph}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — âncora /#faq (usada em Contato) */}
      <section id="faq" className="scroll-mt-28 bg-[#F5F6F0] py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-center text-3xl font-bold text-[#2B5A41] sm:text-4xl">
              Perguntas Frequentes
            </h2>
          </Reveal>
          <div className="mt-10 space-y-5">
            {[
              {
                q: "Mas funciona mesmo online?",
                a: "Sim. A força da constelação está no campo morfogenético, e ele não depende de estarmos fisicamente no mesmo lugar. Com videochamada, avatares e posicionamento no campo, a vivência é tão profunda quanto a presencial.",
              },
              {
                q: "Posso participar sem trazer o meu tema?",
                a: "Sim. Em sessões abertas você pode observar ou representar. É uma forma segura de conhecer a constelação antes de agendar a sua própria sessão.",
              },
              {
                q: "Como agendo e pago minha constelação?",
                a: "Escolha um constelador, selecione dia e horário e siga para o pagamento no checkout. Após a confirmação, a sessão fica disponível no seu painel.",
              },
            ].map((item, index) => (
              <Reveal key={item.q} delay={index * 60}>
                <div className="rounded-2xl border border-[#E0EDE3] bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-[#2B5A41]">{item.q}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#5F7167]">{item.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA grupo WhatsApp — participação gratuita (âncora #grupo-avisos) */}
      <section id="grupo-avisos" className="scroll-mt-28 bg-gradient-to-br from-[#2B5A41] to-[#203C2E] py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-white/80">
              Participar / Assistir — gratuito
            </p>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Vivencie Constelações Online</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-white/90">
              Participe da transformação no conforto de casa — sem custo, pelo grupo de avisos.
            </p>
            <p className="mx-auto mt-2 max-w-xl text-sm text-white/80">
              Entre no grupo do WhatsApp onde avisamos as datas dos encontros e participe da próxima sessão.
            </p>
            <a
              href={WHATSAPP_GROUP}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-[#2B5A41] shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              Entrar no Grupo de Aviso
              <ArrowRight className="h-5 w-5" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default MarketingHome;
